/**
 * Seiten "Anträge" und "Antrag" des Moduls Genehmigung – eine Komponente, damit ein
 * eben angelegter Antrag ohne Neuladen öffnet.
 *
 * Ohne `procedureId` (Anträge): die Liste der Genehmigungsanträge, dort wird angelegt und
 * gelöscht. Mit `procedureId` (Antrag): Der Antragsstatus mit dem nächsten Schritt steht im
 * Seitenkopf (`ProcedurePageHeader`, ADR 011), auf der Seite die Stammdaten, darunter über die
 * volle Breite alle Antragsbausteine als Baum entlang der ELiA-Gliederung mit den Nachweisen,
 * die der Baustein benötigt (ein Nachweis steht unter jedem Baustein, der ihn benötigt).
 * Ein Baustein kommt mit seinem ersten Nachweis in den Antrag.
 *
 * Die Nachweise selbst stehen auf der Seite "Nachweise"; ein Klick auf einen Nachweis im Baum
 * öffnet ihn dort.
 *
 * Die Beteiligten des Antrags (ELiA-Formular 1.1.1) standen hier als dritter Abschnitt und
 * sind aus der Seite genommen. `sections/AntragKontakte.tsx` bleibt vollständig erhalten – der
 * Abschnitt hängt mit dem Einhängen der Komponente wieder an dieser Seite.
 *
 * Die Kosten stehen nicht hier, sondern auf der Seite "Kosten" desselben Antrags.
 *
 * Erreichbar unter `/project/{projectId}/permits` bzw. `/project/{projectId}/permits/{id}/elia`.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Evidence,
  EvidenceCreate,
  ProcedureTransition,
  ProjectDetailPageProps,
} from "../../../types";
import { useAntragNavigation, useEditMode } from "../../layout/ProjectLayout";
import PageTemplate from "../../layout/PageTemplate";
import PermitProceduresSection from "./sections/PermitProceduresSection";
import ApplicationComponentsSection from "./sections/ApplicationComponentsSection";
import NachweisAnlegenDialog from "./sections/NachweisAnlegenDialog";
import { usePermitsData } from "./hooks/usePermitsData";
import { usePageWarning } from "../../../contexts/PageWarningsContext";
import { useAuth } from "../../../auth/AuthProvider";
import { useProjectDeadlines } from "../../../hooks/useProjectDeadlines";
import { manualDeadlinesOf } from "../../../utils/deadlines";
import DeadlinesSection from "../deadlines/DeadlinesSection";
import Section from "../../layout/Section";
import EvidenceHistorie from "./sections/EvidenceHistorie";
import { permitsApi } from "../../../services/api/permits";
import ManualDeadlineDialog from "../deadlines/ManualDeadlineDialog";
import { colors } from "../../../styles/theme";
import { useProcedureActions } from "./sections/useProcedureActions";

interface PermitApplicationsPageProps extends ProjectDetailPageProps {
  /** Der geöffnete Antrag (ELiA); null zeigt die Liste aller Anträge */
  procedureId: string | null;
  canView: boolean;
  canEdit: boolean;
}

/**
 * Feste Höhe des Baums: aufgeklappt wird er sonst so lang, dass die Seite unter ihm verschwindet.
 * Der Baum scrollt in sich.
 */
const SECTION_HEIGHT = 560;

/** Offenes Auswahlmenü „Nachweis zuordnen“ an einem Baustein-Typ */
interface PickerState {
  typeId: string;
  anchor: HTMLElement;
}

const PermitApplicationsPage: React.FC<PermitApplicationsPageProps> = ({
  project,
  procedureId,
  canEdit,
}) => {
  const { isEditMode } = useEditMode();
  const { meldeAntraege, oeffneAntrag, changedProcedure } =
    useAntragNavigation();
  const { user } = useAuth();
  const prefersReducedMotion = useReducedMotion();
  const {
    procedures,
    selectedProcedure,
    selectProcedure,
    components,
    evidences,
    evidencesByComponent,
    procedureTypeOptions,
    componentCategoryOptions,
    componentTypeOptions,
    evidenceTypeOptions,
    processingPathOptions,
    nachweisKatalog,
    orgUsers,
    loading,
    error,
    createProcedure,
    updateProcedure,
    transitionProcedure,
    isSaving,
    saveError,
    deleteProcedure,
    createEvidence,
    setzeBausteine,
    uvpStatusOptions,
    vorhabenbestandteilOptions,
    standortmerkmalOptions,
    windenergiegebietOptions,
    vorbelegung,
    uvpVorbelegungFuer,
    anlageOptionen,
    updateComponent,
    speichern,
    replaceProcedure,
  } = usePermitsData({ projectId: project?.id });

  const editable = canEdit && isEditMode;

  // Auf „Anträge" die Termine des Projekts (Rückfrage beim Löschen), am Antrag seine.
  const deadlines = useProjectDeadlines(project?.id, {
    permitProcedureId: procedureId,
  });
  // Die Antragshistorie lädt neu, wenn sich der Antrag oder seine Termine ändern.
  const historySource = useMemo(
    () => [selectedProcedure, deadlines.deadlines],
    [selectedProcedure, deadlines.deadlines],
  );
  // Ein Übergang ändert die Frist des Antrags – die Termine laden deshalb mit.
  const procedureActions = useProcedureActions(
    useCallback(
      async (id: string, data: ProcedureTransition) => {
        const updated = await transitionProcedure(id, data);
        if (updated) await deadlines.reload();
        return updated;
      },
      [transitionProcedure, deadlines],
    ),
  );

  /** Antrag, an dem gerade „Termin anlegen" aus der Liste offen ist */
  const [deadlineForProcedure, setDeadlineForProcedure] = useState<
    string | null
  >(null);

  // Die URL bestimmt den offenen Antrag – Seitenkopf, Liste und Zurück-Taste führen alle über sie.
  useEffect(() => {
    selectProcedure(procedureId);
  }, [procedureId, selectProcedure]);

  // Ein gelöschter oder fremder Antrag in der URL führt zur Liste statt ins Leere.
  const antragFehlt =
    !loading &&
    !error &&
    procedureId !== null &&
    !procedures.some((procedure) => procedure.id === procedureId);
  useEffect(() => {
    if (antragFehlt) oeffneAntrag(null, undefined, { replace: true });
  }, [antragFehlt, oeffneAntrag]);

  // A transition in the page header changes the procedure and its deadline.
  const deadlinesReload = deadlines.reload;
  useEffect(() => {
    if (!changedProcedure) return;
    replaceProcedure(changedProcedure);
    deadlinesReload();
  }, [changedProcedure, replaceProcedure, deadlinesReload]);

  // Anlegen, Umbenennen und Löschen erscheinen sofort im Seitenkopf.
  useEffect(() => {
    if (!loading) meldeAntraege(procedures);
  }, [loading, procedures, meldeAntraege]);

  const { setWarning } = usePageWarning("project-permit-applications");

  useEffect(() => {
    setWarning(false);
  }, [setWarning]);

  /** Aktiviert oder deaktiviert einen Antragsbaustein; meldet sich selbst über PageTemplate. */
  const handleToggleAktiv = useCallback(
    (componentId: string, istAktiv: boolean) =>
      updateComponent(componentId, { ist_aktiv: istAktiv }),
    [updateComponent],
  );

  /** Öffnet einen Nachweis aus dem Baum auf der Seite "Nachweise" desselben Antrags */
  const oeffneNachweis = useCallback(
    (evidenceId: string) => {
      if (procedureId) {
        oeffneAntrag(procedureId, "project-permit-evidences", {
          nachweisId: evidenceId,
        });
      }
    },
    [procedureId, oeffneAntrag],
  );

  const [picker, setPicker] = useState<PickerState | null>(null);
  /** Baustein-Typ, an dem der Dialog „Nachweis anlegen“ gerade einen Nachweis anlegt */
  const [anlegenAnTyp, setAnlegenAnTyp] = useState<string | null>(null);

  /** Baustein-Typen, die einen Nachweis heute benötigen */
  const typenVon = (evidence: Evidence): string[] =>
    evidence.application_components.map(
      (ref) => ref.application_component_type_id,
    );

  /**
   * Legt einen Nachweis an und ordnet ihn den gewählten Baustein-Typen zu – vorbelegt mit dem
   * Baustein, an dem der Dialog geöffnet wurde. Fehlt ein Baustein noch im Antrag, nimmt ihn der
   * Server dabei auf (eine Transaktion).
   */
  const handleAnlegen = async (data: EvidenceCreate): Promise<boolean> => {
    const created = await speichern(() => createEvidence(data));
    return !!created;
  };

  /** Nachweise des Antrags, die der gewählte Baustein noch nicht benötigt */
  const zuordnungsKandidaten = picker
    ? evidences.filter((e) => !typenVon(e).includes(picker.typeId))
    : [];

  const handlePickEvidenceAssign = async (evidenceId: string) => {
    const typeId = picker?.typeId;
    setPicker(null);
    const evidence = evidences.find((e) => e.id === evidenceId);
    if (!typeId || !evidence) return;
    await setzeBausteine(evidenceId, [...typenVon(evidence), typeId]);
  };

  const handleUnassign = async (typeId: string, evidenceId: string) => {
    const evidence = evidences.find((e) => e.id === evidenceId);
    if (!evidence) return;
    await setzeBausteine(
      evidenceId,
      typenVon(evidence).filter((id) => id !== typeId),
    );
  };

  /**
   * Ein- und Ausblenden der antragsgebundenen Abschnitte. Beide Blöcke – die Bausteine und die
   * Beteiligten darunter – nutzen dieselbe Bewegung, damit sie beim Wechsel des Antrags als
   * ein Vorgang wirken.
   */
  const antragBewegung = {
    initial: prefersReducedMotion
      ? { opacity: 0 }
      : { opacity: 0, y: 32, scaleY: 0.98 },
    animate: { opacity: 1, y: 0, scaleY: 1 },
    exit: prefersReducedMotion
      ? { opacity: 0 }
      : { opacity: 0, y: -16, scaleY: 0.98 },
    transition: prefersReducedMotion
      ? { duration: 0.15 }
      : ({ type: "spring", stiffness: 260, damping: 26 } as const),
    style: { transformOrigin: "top center" },
  };

  return (
    <PageTemplate
      isLoading={loading}
      error={error}
      isSaving={isSaving}
      saveError={saveError}
    >
      <Box>
        {/*
          Erst rendern, wenn die Optionen (u.a. Anlagenzahl für die Vorbelegung in
          VerfahrenFormular) geladen sind. PageTemplate legt den Spinner ohnehin schon als
          Overlay über den Inhalt – das Verzögern ändert optisch nichts während des Ladens,
          verhindert aber, dass das Formular mit noch leeren Options-Listen mountet: seine
          Vorbelegung steht in useState-Initializern, die nur beim allerersten Mount laufen,
          und ohne diese Bedingung mountet die Sektion sofort mit procedures=[] (→ isEmpty)
          und leeren Optionen, bevor die Daten da sind – und bleibt es, weil isEmpty vor und
          nach dem Laden gleich bleibt und daher kein Remount stattfindet.
        */}
        {!loading && (
          <PermitProceduresSection
            procedures={procedures}
            // Auf "Anträge" bleibt die Liste stehen, auch wenn der Hook nach dem Anlegen
            // schon den neuen Antrag hält.
            selectedProcedure={procedureId ? selectedProcedure : null}
            procedureTypeOptions={procedureTypeOptions}
            orgUsers={orgUsers}
            uvpStatusOptions={uvpStatusOptions}
            vorhabenbestandteilOptions={vorhabenbestandteilOptions}
            anlageOptionen={anlageOptionen}
            standortmerkmalOptions={standortmerkmalOptions}
            windenergiegebietOptions={windenergiegebietOptions}
            vorbelegung={vorbelegung}
            uvpVorbelegungFuer={uvpVorbelegungFuer}
            canEdit={editable}
            onSelect={(id) => oeffneAntrag(id)}
            onAnlageAbgeschlossen={(procedure) =>
              oeffneAntrag(procedure.id, "project-permit-evidences")
            }
            onCreate={createProcedure}
            onUpdate={updateProcedure}
            onDelete={async (id) => {
              await deleteProcedure(id);
              await deadlines.reload();
            }}
            onAction={procedureActions.openAction}
            onAddDeadline={setDeadlineForProcedure}
            manualDeadlineCount={(id) =>
              manualDeadlinesOf(deadlines.deadlines, { permitProcedureId: id })
                .length
            }
            aktuellerBenutzerId={user?.id}
            projectId={project?.id}
          />
        )}
      </Box>

      <AnimatePresence initial={false}>
        {procedureId && selectedProcedure && (
          <motion.div
            key={`bausteine-${selectedProcedure.id}`}
            {...antragBewegung}
          >
            {/* Der Baum läuft mit `noOuterPadding` (feste Höhe); den Abstand nach unten setzt
                dieser Rahmen – dieselben 3 wie `Section` sonst überall. */}
            <Box sx={{ mb: 3 }}>
              <ApplicationComponentsSection
                components={components}
                componentTypeOptions={componentTypeOptions}
                componentCategoryOptions={componentCategoryOptions}
                evidencesByComponent={evidencesByComponent}
                canEdit={editable}
                // Ein Nachweis wird hier nicht geöffnet, sondern auf der Seite "Nachweise".
                selectedEvidenceId={null}
                onSelectEvidence={oeffneNachweis}
                onAddEvidence={(typeId, anchor, modus) =>
                  modus === "zuordnen"
                    ? setPicker({ typeId, anchor })
                    : setAnlegenAnTyp(typeId)
                }
                onUnassignEvidence={handleUnassign}
                onToggleAktiv={handleToggleAktiv}
                height={SECTION_HEIGHT}
              />
            </Box>
            {project && (
              <DeadlinesSection
                projectId={project.id}
                controller={deadlines}
                canEdit={editable}
                permitProcedureId={selectedProcedure.id}
              />
            )}
            {project && (
              <Section title="Historie" noOuterPadding>
                {/* Neu geladen, wenn sich der Antrag oder seine Termine ändern – beides hinterlässt
                    einen Eintrag. Dieselbe Darstellung wie die Nachweishistorie, ohne Status. */}
                <EvidenceHistorie
                  source={historySource}
                  ladeHistorie={() =>
                    permitsApi
                      .getPermitProcedureHistory(
                        project.id,
                        selectedProcedure.id,
                      )
                      .then((eintraege) =>
                        eintraege.map((eintrag) => ({
                          id: eintrag.id,
                          aktion: eintrag.action,
                          aktion_label: eintrag.action_label,
                          aenderungen: eintrag.changes.map((change) => ({
                            feld: change.field,
                            label: change.label,
                            alt: change.old,
                            neu: change.new,
                          })),
                          kommentar: eintrag.comment,
                          benutzer: eintrag.user,
                          erstellt_am: eintrag.created_at,
                        })),
                      )
                  }
                />
              </Section>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <Menu
        open={!!picker}
        anchorEl={picker?.anchor ?? null}
        onClose={() => setPicker(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { maxHeight: 340, width: 340 } } }}
      >
        {picker &&
          (zuordnungsKandidaten.length === 0 ? (
            <MenuItem disabled>
              <Typography variant="body2" noWrap>
                Kein weiterer Nachweis am Antrag vorhanden
              </Typography>
            </MenuItem>
          ) : (
            zuordnungsKandidaten.map((kandidat) => (
              <MenuItem
                key={kandidat.id}
                onClick={() => handlePickEvidenceAssign(kandidat.id)}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {kandidat.bezeichnung || "Ohne Bezeichnung"}
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: colors.text.secondary, display: "block" }}
                  >
                    {kandidat.evidence_type.name}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          ))}
      </Menu>

      {procedureActions.elements}
      {project && (
        <ManualDeadlineDialog
          open={!!deadlineForProcedure}
          projectId={project.id}
          presetPermitProcedureId={deadlineForProcedure}
          onClose={() => setDeadlineForProcedure(null)}
          onSubmit={deadlines.submit}
        />
      )}
      <NachweisAnlegenDialog
        open={!!anlegenAnTyp}
        nachweisKatalog={nachweisKatalog}
        evidenceTypeOptions={evidenceTypeOptions}
        processingPathOptions={processingPathOptions}
        orgUsers={orgUsers}
        componentTypeOptions={componentTypeOptions}
        vorbelegteBausteinTypIds={anlegenAnTyp ? [anlegenAnTyp] : undefined}
        aktuellerBenutzerId={user?.id}
        onClose={() => setAnlegenAnTyp(null)}
        onSubmit={handleAnlegen}
      />
    </PageTemplate>
  );
};

export default PermitApplicationsPage;
